// IntentEditor - Editeur d'intentions configurable
import { useState, useEffect } from "react";
import {
  Brain,
  Plus,
  Save,
  Trash2,
  Copy,
  Play,
  X,
  ChevronDown,
  ChevronUp,
  GripVertical,
  MessageCircle,
  AlertTriangle,
  UserCheck,
  Briefcase,
  Headphones,
  Info,
  Calendar,
  AlertCircle,
  MessageSquare,
  Smartphone,
  PhoneForwarded,
  Globe,
  FileText,
  ArrowUpCircle,
  Loader2,
  Check,
  Zap,
  FileJson,
  Download
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  "general": MessageCircle,
  "urgence": AlertTriangle,
  "suivi": UserCheck,
  "commercial": Briefcase,
  "support": Headphones,
  "information": Info,
  "rdv": Calendar,
  "reclamation": AlertCircle
};

const ACTION_ICONS = {
  "respond": MessageSquare,
  "sms": Smartphone,
  "transfer": PhoneForwarded,
  "webhook": Globe,
  "log": FileText,
  "escalate": ArrowUpCircle
};

export default function IntentEditor({ agentId, agentName, onClose }) {
  const [intents, setIntents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingIntent, setEditingIntent] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testText, setTestText] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  
  // JSON Config state
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [jsonConfig, setJsonConfig] = useState(null);
  const [loadingJson, setLoadingJson] = useState(false);
  
  // Scripts viewer state
  const [showScriptsModal, setShowScriptsModal] = useState(false);
  const [scriptsData, setScriptsData] = useState(null);
  const [loadingScripts, setLoadingScripts] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "general",
    priority: 0,
    is_active: true,
    keywords: [],
    keyword_match_mode: "any",
    patterns: [],
    phrases: [],
    conditions: {},
    action_type: "respond",
    action_config: {},
    response_template: ""
  });
  
  const [keywordInput, setKeywordInput] = useState("");
  const [patternInput, setPatternInput] = useState("");
  const [phraseInput, setPhraseInput] = useState("");

  useEffect(() => {
    fetchIntents();
    fetchMetadata();
  }, [agentId]);

  const fetchIntents = async () => {
    try {
      const response = await axios.get(`${API}/api/intents/${agentId}`, {
        withCredentials: true
      });
      setIntents(response.data.intents || []);
    } catch (error) {
      console.error("Error fetching intents:", error);
      toast.error("Erreur lors du chargement des intents");
    } finally {
      setLoading(false);
    }
  };

  const fetchMetadata = async () => {
    try {
      const [catRes, actRes] = await Promise.all([
        axios.get(`${API}/api/intents/meta/categories`, { withCredentials: true }),
        axios.get(`${API}/api/intents/meta/actions`, { withCredentials: true })
      ]);
      setCategories(catRes.data.categories || []);
      setActionTypes(actRes.data.actions || []);
    } catch (error) {
      console.error("Error fetching metadata:", error);
    }
  };

  const fetchJsonConfig = async () => {
    setLoadingJson(true);
    try {
      // Utilise l'endpoint spécifique à l'agent
      const response = await axios.get(`${API}/api/agents/${agentId}/config-raw`, { withCredentials: true });
      setJsonConfig(response.data);
    } catch (error) {
      console.error("Error fetching JSON config:", error);
      toast.error("Erreur lors du chargement de la configuration");
    } finally {
      setLoadingJson(false);
    }
  };

  const handleCopyJson = () => {
    if (jsonConfig?.content) {
      navigator.clipboard.writeText(jsonConfig.content);
      toast.success("Configuration copiée dans le presse-papier");
    }
  };

  const handleDownloadJson = () => {
    if (jsonConfig?.content) {
      const blob = new Blob([jsonConfig.content], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = jsonConfig.filename || 'audrey_config.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Fichier téléchargé");
    }
  };

  const fetchScripts = async () => {
    setLoadingScripts(true);
    try {
      console.log("Fetching scripts for agent:", agentId);
      const response = await axios.get(`${API}/api/agents/${agentId}/scripts`, { withCredentials: true });
      console.log("Scripts response:", response.data);
      setScriptsData(response.data);
    } catch (error) {
      console.error("Error fetching scripts:", error);
      toast.error("Erreur lors du chargement des scripts");
    } finally {
      setLoadingScripts(false);
    }
  };

  const handleCopyScripts = () => {
    if (scriptsData?.scripts) {
      const formattedScripts = JSON.stringify(scriptsData.scripts, null, 2);
      navigator.clipboard.writeText(formattedScripts);
      toast.success("Scripts copiés dans le presse-papier");
    }
  };

  const handleDownloadScripts = () => {
    if (scriptsData?.scripts) {
      const formattedScripts = JSON.stringify({ scripts: scriptsData.scripts }, null, 2);
      const blob = new Blob([formattedScripts], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scripts_${agentName || 'agent'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Scripts téléchargés");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      category: "general",
      priority: 0,
      is_active: true,
      keywords: [],
      keyword_match_mode: "any",
      patterns: [],
      phrases: [],
      conditions: {},
      action_type: "respond",
      action_config: {},
      response_template: ""
    });
    setKeywordInput("");
    setPatternInput("");
    setPhraseInput("");
  };

  const openEditModal = (intent) => {
    setFormData({
      name: intent.name || "",
      description: intent.description || "",
      category: intent.category || "general",
      priority: intent.priority || 0,
      is_active: intent.is_active !== false,
      keywords: intent.keywords || [],
      keyword_match_mode: intent.keyword_match_mode || "any",
      patterns: intent.patterns || [],
      phrases: intent.phrases || [],
      conditions: intent.conditions || {},
      action_type: intent.action_type || "respond",
      action_config: intent.action_config || {},
      response_template: intent.response_template || ""
    });
    setEditingIntent(intent);
    setShowCreateModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Le nom est requis");
      return;
    }

    setSaving(true);
    try {
      if (editingIntent) {
        await axios.put(`${API}/api/intents/detail/${editingIntent.id}`, formData, {
          withCredentials: true
        });
        toast.success("Intent mis a jour");
      } else {
        await axios.post(`${API}/api/intents/${agentId}`, formData, {
          withCredentials: true
        });
        toast.success("Intent créé");
      }
      
      setShowCreateModal(false);
      setEditingIntent(null);
      resetForm();
      fetchIntents();
    } catch (error) {
      console.error("Error saving intent:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (intentId) => {
    if (!confirm("Supprimer cet intent ?")) return;
    
    try {
      await axios.delete(`${API}/api/intents/detail/${intentId}`, {
        withCredentials: true
      });
      toast.success("Intent supprime");
      fetchIntents();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleDuplicate = async (intentId) => {
    try {
      await axios.post(`${API}/api/intents/detail/${intentId}/duplicate`, {}, {
        withCredentials: true
      });
      toast.success("Intent duplique");
      fetchIntents();
    } catch (error) {
      toast.error("Erreur lors de la duplication");
    }
  };

  const handleToggleActive = async (intent) => {
    try {
      await axios.put(`${API}/api/intents/detail/${intent.id}`, {
        is_active: !intent.is_active
      }, { withCredentials: true });
      fetchIntents();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const handleTest = async () => {
    if (!testText.trim()) return;
    
    setTesting(true);
    setTestResult(null);
    
    try {
      const response = await axios.post(`${API}/api/intents/${agentId}/test`, {
        text: testText
      }, { withCredentials: true });
      
      setTestResult(response.data);
    } catch (error) {
      toast.error("Erreur lors du test");
    } finally {
      setTesting(false);
    }
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData({...formData, keywords: [...formData.keywords, keywordInput.trim()]});
      setKeywordInput("");
    }
  };

  const removeKeyword = (kw) => {
    setFormData({...formData, keywords: formData.keywords.filter(k => k !== kw)});
  };

  const addPattern = () => {
    if (patternInput.trim() && !formData.patterns.includes(patternInput.trim())) {
      setFormData({...formData, patterns: [...formData.patterns, patternInput.trim()]});
      setPatternInput("");
    }
  };

  const removePattern = (p) => {
    setFormData({...formData, patterns: formData.patterns.filter(x => x !== p)});
  };

  const addPhrase = () => {
    if (phraseInput.trim() && !formData.phrases.includes(phraseInput.trim())) {
      setFormData({...formData, phrases: [...formData.phrases, phraseInput.trim()]});
      setPhraseInput("");
    }
  };

  const removePhrase = (p) => {
    setFormData({...formData, phrases: formData.phrases.filter(x => x !== p)});
  };

  const getCategoryIcon = (catId) => {
    const Icon = CATEGORY_ICONS[catId] || MessageCircle;
    return Icon;
  };

  const getActionIcon = (actionId) => {
    const Icon = ACTION_ICONS[actionId] || MessageSquare;
    return Icon;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Brain className="w-6 h-6 text-purple-400" />
            Intent Engine - {agentName}
          </h2>
          <p className="text-zinc-400 text-sm">Configurez les intentions détectables par l'agent</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={() => { fetchScripts(); setShowScriptsModal(true); }}
            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            Voir Scripts
          </Button>
          <Button 
            variant="outline" 
            onClick={() => { fetchJsonConfig(); setShowJsonModal(true); }}
            className="border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
          >
            <FileJson className="w-4 h-4 mr-2" />
            Config JSON
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowTestModal(true)}
            className="border-white/10"
          >
            <Play className="w-4 h-4 mr-2" />
            Tester
          </Button>
          <Button 
            onClick={() => { resetForm(); setEditingIntent(null); setShowCreateModal(true); }}
            className="bg-purple-500 hover:bg-purple-600"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvel intent
          </Button>
          {onClose && (
            <Button variant="ghost" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Liste des intents */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-zinc-500" />
        </div>
      ) : intents.length === 0 ? (
        <Card className="bg-zinc-900/50 border-white/10">
          <CardContent className="py-12 text-center">
            <Brain className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <p className="text-zinc-400">Aucun intent configure</p>
            <p className="text-zinc-500 text-sm mt-1">Creez votre premier intent pour commencer</p>
            <Button 
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              className="mt-4 bg-purple-500 hover:bg-purple-600"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer un intent
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {intents.map((intent, index) => {
            const CategoryIcon = getCategoryIcon(intent.category);
            const ActionIcon = getActionIcon(intent.action_type);
            
            return (
              <Card 
                key={intent.id} 
                className={`bg-zinc-900/50 border-white/10 ${!intent.is_active ? 'opacity-50' : ''}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2 text-zinc-500">
                        <GripVertical className="w-4 h-4" />
                        <span className="text-xs font-mono">#{index + 1}</span>
                      </div>
                      
                      <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                        <CategoryIcon className="w-5 h-5 text-purple-400" />
                      </div>
                      
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-white">{intent.name}</h3>
                          <Badge variant="secondary" className="bg-zinc-700 text-zinc-300 text-xs">
                            {intent.category}
                          </Badge>
                          {intent.priority > 0 && (
                            <Badge variant="secondary" className="bg-amber-500/20 text-amber-400 text-xs">
                              P{intent.priority}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-zinc-500 mt-0.5">{intent.description}</p>
                        
                        {/* Détection info */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                          {intent.keywords?.length > 0 && (
                            <span>{intent.keywords.length} keywords</span>
                          )}
                          {intent.patterns?.length > 0 && (
                            <span>{intent.patterns.length} patterns</span>
                          )}
                          {intent.phrases?.length > 0 && (
                            <span>{intent.phrases.length} phrases</span>
                          )}
                          <span className="flex items-center gap-1">
                            <ActionIcon className="w-3 h-3" />
                            {intent.action_type}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={intent.is_active}
                        onCheckedChange={() => handleToggleActive(intent)}
                      />
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDuplicate(intent.id)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditModal(intent)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Zap className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleDelete(intent.id)}
                        className="text-zinc-400 hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal Creation/Edition */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto pr-6">
          <DialogHeader>
            <DialogTitle>{editingIntent ? "Modifier l'intent" : "Créer un intent"}</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Configurez la détection et l'action associée
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="general" className="mt-4">
            <TabsList className="bg-zinc-800 w-full justify-start">
              <TabsTrigger value="general">Général</TabsTrigger>
              <TabsTrigger value="detection">Détection</TabsTrigger>
              <TabsTrigger value="action">Action</TabsTrigger>
            </TabsList>
            
            {/* General Tab */}
            <TabsContent value="general" className="space-y-4 mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Nom *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ex: Demande urgente"
                    className="bg-zinc-800 border-white/10 mt-1"
                  />
                </div>
                <div>
                  <Label>Categorie</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger className="bg-zinc-800 border-white/10 mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-white/10">
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id} className="text-white">
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Decrivez quand cet intent doit etre detecte..."
                  className="bg-zinc-800 border-white/10 mt-1"
                />
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Priorité</Label>
                  <Input
                    type="number"
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: parseInt(e.target.value) || 0})}
                    className="bg-zinc-800 border-white/10 mt-1"
                  />
                  <p className="text-xs text-zinc-500 mt-1">Plus eleve = evalue en premier</p>
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData({...formData, is_active: v})}
                  />
                  <Label>Intent actif</Label>
                </div>
              </div>
            </TabsContent>
            
            {/* Détection Tab */}
            <TabsContent value="detection" className="space-y-6 mt-4">
              {/* Keywords */}
              <div>
                <Label className="flex items-center gap-2">
                  <span>Mots-cles</span>
                  <Badge variant="secondary" className="bg-zinc-700 text-zinc-300 text-xs">
                    {formData.keywords.length}
                  </Badge>
                </Label>
                <p className="text-xs text-zinc-500 mb-2">L'intent est detecte si ces mots sont presents</p>
                <div className="flex gap-2">
                  <Input
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addKeyword())}
                    placeholder="Ajouter un mot-cle..."
                    className="bg-zinc-800 border-white/10"
                  />
                  <Button onClick={addKeyword} variant="outline" className="border-white/10">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.keywords.map((kw, i) => (
                    <Badge key={i} variant="secondary" className="bg-purple-500/20 text-purple-300">
                      {kw}
                      <button onClick={() => removeKeyword(kw)} className="ml-1 hover:text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="mt-2">
                  <Label className="text-xs text-zinc-500">Mode de correspondance</Label>
                  <Select value={formData.keyword_match_mode} onValueChange={(v) => setFormData({...formData, keyword_match_mode: v})}>
                    <SelectTrigger className="bg-zinc-800 border-white/10 mt-1 w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-white/10">
                      <SelectItem value="any" className="text-white">Au moins un (OR)</SelectItem>
                      <SelectItem value="all" className="text-white">Tous (AND)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Patterns */}
              <div>
                <Label className="flex items-center gap-2">
                  <span>Patterns (Regex)</span>
                  <Badge variant="secondary" className="bg-zinc-700 text-zinc-300 text-xs">
                    {formData.patterns.length}
                  </Badge>
                </Label>
                <p className="text-xs text-zinc-500 mb-2">Expressions regulieres pour détection avancee</p>
                <div className="flex gap-2">
                  <Input
                    value={patternInput}
                    onChange={(e) => setPatternInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPattern())}
                    placeholder="Ex: urgent.*rappel"
                    className="bg-zinc-800 border-white/10 font-mono text-sm"
                  />
                  <Button onClick={addPattern} variant="outline" className="border-white/10">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.patterns.map((p, i) => (
                    <Badge key={i} variant="secondary" className="bg-blue-500/20 text-blue-300 font-mono text-xs">
                      {p}
                      <button onClick={() => removePattern(p)} className="ml-1 hover:text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
              
              {/* Phrases */}
              <div>
                <Label className="flex items-center gap-2">
                  <span>Phrases exactes</span>
                  <Badge variant="secondary" className="bg-zinc-700 text-zinc-300 text-xs">
                    {formData.phrases.length}
                  </Badge>
                </Label>
                <p className="text-xs text-zinc-500 mb-2">Phrases completes a detecter</p>
                <div className="flex gap-2">
                  <Input
                    value={phraseInput}
                    onChange={(e) => setPhraseInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPhrase())}
                    placeholder="Ex: Je souhaite annuler ma commande"
                    className="bg-zinc-800 border-white/10"
                  />
                  <Button onClick={addPhrase} variant="outline" className="border-white/10">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.phrases.map((p, i) => (
                    <Badge key={i} variant="secondary" className="bg-emerald-500/20 text-emerald-300">
                      "{p}"
                      <button onClick={() => removePhrase(p)} className="ml-1 hover:text-white">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            </TabsContent>
            
            {/* Action Tab */}
            <TabsContent value="action" className="space-y-4 mt-4">
              <div>
                <Label>Type d'action</Label>
                <Select value={formData.action_type} onValueChange={(v) => setFormData({...formData, action_type: v})}>
                  <SelectTrigger className="bg-zinc-800 border-white/10 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-white/10">
                    {actionTypes.map(action => {
                      const Icon = getActionIcon(action.id);
                      return (
                        <SelectItem key={action.id} value={action.id} className="text-white">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            {action.name}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              
              {formData.action_type === "respond" && (
                <div>
                  <Label>Template de reponse</Label>
                  <Textarea
                    value={formData.response_template}
                    onChange={(e) => setFormData({...formData, response_template: e.target.value})}
                    placeholder="La reponse que l'agent donnera..."
                    className="bg-zinc-800 border-white/10 mt-1"
                    rows={4}
                  />
                </div>
              )}
              
              {formData.action_type === "sms" && (
                <div>
                  <Label>Template SMS</Label>
                  <Textarea
                    value={formData.action_config.sms_template || ""}
                    onChange={(e) => setFormData({...formData, action_config: {...formData.action_config, sms_template: e.target.value}})}
                    placeholder="Contenu du SMS..."
                    className="bg-zinc-800 border-white/10 mt-1"
                  />
                </div>
              )}
              
              {formData.action_type === "transfer" && (
                <div>
                  <Label>Numero de transfert</Label>
                  <Input
                    value={formData.action_config.transfer_number || ""}
                    onChange={(e) => setFormData({...formData, action_config: {...formData.action_config, transfer_number: e.target.value}})}
                    placeholder="+33..."
                    className="bg-zinc-800 border-white/10 mt-1"
                  />
                </div>
              )}
              
              {formData.action_type === "webhook" && (
                <div>
                  <Label>URL du webhook</Label>
                  <Input
                    value={formData.action_config.url || ""}
                    onChange={(e) => setFormData({...formData, action_config: {...formData.action_config, url: e.target.value}})}
                    placeholder="https://..."
                    className="bg-zinc-800 border-white/10 mt-1"
                  />
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowCreateModal(false)} className="border-white/10">
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-500 hover:bg-purple-600">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              {editingIntent ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Test */}
      <Dialog open={showTestModal} onOpenChange={setShowTestModal}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>Tester la détection d'intent</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Texte a analyser</Label>
              <Textarea
                value={testText}
                onChange={(e) => setTestText(e.target.value)}
                placeholder="Entrez le texte que dirait un appelant..."
                className="bg-zinc-800 border-white/10 mt-1"
              />
            </div>
            
            <Button onClick={handleTest} disabled={testing} className="w-full bg-purple-500 hover:bg-purple-600">
              {testing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
              Tester
            </Button>
            
            {testResult && (
              <div className={`p-4 rounded-lg ${testResult.detected ? 'bg-emerald-500/20 border border-emerald-500/50' : 'bg-zinc-800'}`}>
                {testResult.detected ? (
                  <div>
                    <div className="flex items-center gap-2 text-emerald-400 mb-2">
                      <Check className="w-5 h-5" />
                      <span className="font-medium">Intent detecté!</span>
                    </div>
                    <p className="text-white">{testResult.intent?.name}</p>
                    <p className="text-zinc-400 text-sm">{testResult.intent?.description}</p>
                    <div className="mt-2 text-xs text-zinc-500">
                      Action: {testResult.intent?.action_type}
                    </div>
                  </div>
                ) : (
                  <div className="text-zinc-400">
                    Aucun intent détecté pour ce texte
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* JSON Config Modal - Compact */}
      <Dialog open={showJsonModal} onOpenChange={setShowJsonModal}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-white flex items-center gap-2 text-base">
              <FileJson className="w-4 h-4 text-amber-400" />
              Config JSON - {agentName}
            </DialogTitle>
          </DialogHeader>
          
          {loadingJson ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
            </div>
          ) : jsonConfig ? (
            <div className="space-y-3">
              {/* Info fichier + Actions */}
              <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg text-xs">
                <span className="text-zinc-400">📄 {jsonConfig.filename} ({(jsonConfig.size_bytes / 1024).toFixed(1)} KB)</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleCopyJson} className="border-white/10 h-7 text-xs px-2">
                    <Copy className="w-3 h-3 mr-1" />
                    Copier
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleDownloadJson} className="border-white/10 h-7 text-xs px-2">
                    <Download className="w-3 h-3 mr-1" />
                    Télécharger
                  </Button>
                </div>
              </div>
              
              {/* Contenu JSON - hauteur réduite */}
              <pre className="bg-zinc-950 p-3 rounded-lg overflow-auto max-h-[35vh] text-xs font-mono text-zinc-300 border border-white/5">
                {jsonConfig.content}
              </pre>
              
              {/* Instructions compactes */}
              <p className="text-xs text-amber-400/80 italic">
                💡 Copiez/téléchargez le fichier, modifiez-le et renvoyez-le pour déploiement
              </p>
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-400 text-sm">
              Impossible de charger la configuration
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Scripts Viewer Modal */}
      <Dialog open={showScriptsModal} onOpenChange={setShowScriptsModal}>
        <DialogContent className="bg-zinc-900 border-white/10 max-w-3xl max-h-[90vh]">
          <DialogHeader className="pb-2">
            <DialogTitle className="text-white flex items-center gap-2 text-base">
              <MessageSquare className="w-4 h-4 text-emerald-400" />
              Scripts de Réponse - {agentName}
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-sm">
              Textes prononcés par l'agent selon l'intention détectée
            </DialogDescription>
          </DialogHeader>
          
          {loadingScripts ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
            </div>
          ) : scriptsData?.scripts ? (
            <div className="space-y-3">
              {/* Actions */}
              <div className="flex items-center justify-between p-2 bg-zinc-800/50 rounded-lg text-xs">
                <span className="text-zinc-400">
                  📜 {Object.keys(scriptsData.scripts).length} intentions configurées
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleCopyScripts} className="border-white/10 h-7 text-xs px-2">
                    <Copy className="w-3 h-3 mr-1" />
                    Copier
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleDownloadScripts} className="border-white/10 h-7 text-xs px-2">
                    <Download className="w-3 h-3 mr-1" />
                    Télécharger
                  </Button>
                </div>
              </div>
              
              {/* Liste des scripts par intent */}
              <div className="overflow-auto max-h-[55vh] space-y-3 pr-2">
                {Object.entries(scriptsData.scripts).map(([intentKey, scriptObj]) => (
                  <div key={intentKey} className="bg-zinc-800/50 rounded-lg border border-white/5 overflow-hidden">
                    {/* Header de l'intent */}
                    <div className="px-3 py-2 bg-zinc-800 border-b border-white/5 flex items-center gap-2">
                      <Badge className="bg-emerald-500/20 text-emerald-400 text-xs font-mono">
                        {intentKey}
                      </Badge>
                      <span className="text-zinc-500 text-xs">
                        {Object.keys(scriptObj).length} scripts
                      </span>
                    </div>
                    
                    {/* Scripts de cet intent */}
                    <div className="p-3 space-y-2">
                      {Object.entries(scriptObj).map(([scriptName, scriptText]) => (
                        <div key={scriptName} className="flex gap-3">
                          <span className="text-purple-400 text-xs font-medium min-w-[120px] pt-0.5">
                            {scriptName}:
                          </span>
                          <p className="text-zinc-300 text-sm flex-1">
                            "{scriptText}"
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Instructions */}
              <p className="text-xs text-emerald-400/80 italic">
                💡 Copiez/téléchargez les scripts, modifiez-les et renvoyez-les pour mise à jour
              </p>
            </div>
          ) : (
            <div className="text-center py-8 text-zinc-400 text-sm">
              Aucun script configuré pour cet agent
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
